import React, { useState, useEffect, useRef } from 'react';


interface AnimatedTextProps {
  content: string;
  delay?: number;
  className?: string;
  onComplete?: () => void;
  debugId?: string;
}

const AnimatedText: React.FC<AnimatedTextProps> = ({
  content,
  delay = 200,
  className = "text-gray-900",
  onComplete,
  debugId
}) => {
  // Split content into lines and words
  const lines = content.split(/\r?\n/);
  const wordsPerLine = lines.map(line => line.split(/(\s+)/));
  const flatWords = wordsPerLine.flat();
  const totalWords = flatWords.length;
  

  const [visibleWordCount, setVisibleWordCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  
  // Keep the ref updated with the latest callback
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setVisibleWordCount(0);
    let interval: NodeJS.Timeout;
    let startTimeout: NodeJS.Timeout;
    let completed = false;

    // If no words, complete immediately
    if (totalWords === 0) {
      const emptyTimeout = setTimeout(() => {
        if (onCompleteRef.current) onCompleteRef.current();
      }, delay);
      return () => clearTimeout(emptyTimeout);
    }

    startTimeout = setTimeout(() => {
      interval = setInterval(() => {
        setVisibleWordCount(prev => {
          if (prev < totalWords) {
            return prev + 1;
          } else {
            if (!completed) {
              completed = true;
              if (onCompleteRef.current) onCompleteRef.current();
            }
            clearInterval(interval);
            return prev;
          }
        });
      }, 40); // 40ms per word
    }, delay);

    return () => {
      clearTimeout(startTimeout);
      clearInterval(interval);
    };
  }, [content, delay, totalWords, debugId]);

  // Helper to render only visible words, with optional fade-in
  let wordIndex = 0;
  return (
    <div className="whitespace-pre-wrap">
      {wordsPerLine.map((words, lineIdx) => (
        <div key={lineIdx} style={{ minHeight: words.length === 0 || (words.length === 1 && words[0] === '') ? '2em' : 'auto' }}>
          {words.map((word, i) => {
            const isVisible = wordIndex < visibleWordCount;
            const span = (
              <span
                key={`${lineIdx}-${i}`}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease-out',
                  display: 'inline',
                  userSelect: 'text',
                }}
                className={className}
              >
                {word}
              </span>
            );
            wordIndex++;
            return span;
          })}
        </div>
      ))}
    </div>
  );
};

export default AnimatedText;